
 // =========================================================
// LA ESQUINA DEL PARQUES
// BAR · GASTROBAR · EXPERIENCIA Y SABOR
// =========================================================
//
// CatalogPage.jsx
//
// CATÁLOGO PREMIUM · HERO EDITORIAL
//
// ✓ Hero inspirado en el diseño del Blog
// ✓ Imagen configurable desde el backend
// ✓ Overlay para mejorar la legibilidad
// ✓ Productos, bebidas y comidas
// ✓ Filtros y ordenamiento
// ✓ Descuentos y stock
// ✓ Carrito con localStorage
// ✓ Evento cartUpdated
// ✓ API /products/
// ✓ Responsive
//
// =========================================================

import { useEffect, useMemo, useState } from "react";

import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  Package,
  Search,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Tag,
  X,
} from "lucide-react";

import {
  Link,
  useSearchParams,
} from "react-router-dom";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";
import API from "../../services/api";
import catalogBg from "../../assets/bg.png";

import "../../styles/catalog.css";

// =========================================================
// HELPERS
// =========================================================

function formatPrice(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "string" && value.includes("$")) {
    return value;
  }

  const number = Number(value);

  if (Number.isNaN(number)) {
    return String(value);
  }

  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(number);
}

function getProductImage(product) {
  if (!product) return catalogBg;

  const image =
    product.image ||
    product.image_url ||
    product.photo ||
    product.thumbnail ||
    "";

  if (typeof image === "string" && image.trim()) {
    return image;
  }

  if (typeof image === "object" && image !== null) {
    return (
      image.image ||
      image.image_url ||
      image.url ||
      image.src ||
      catalogBg
    );
  }

  return catalogBg;
}

function getProductCategory(product) {
  if (!product) return "GENERAL";

  const rawCategory =
    product.category_name ||
    product.category ||
    "";

  if (typeof rawCategory === "string") {
    return rawCategory || "GENERAL";
  }

  if (typeof rawCategory === "object" && rawCategory !== null) {
    return rawCategory.name || rawCategory.title || "GENERAL";
  }

  return "GENERAL";
}

function normalizeCategory(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function getResults(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.products)) return data.products;
  return [];
}

// =========================================================
// CATEGORIES
// =========================================================

const CATEGORY_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "cervezas", label: "Cervezas" },
  { value: "cocteles", label: "Cócteles" },
  { value: "bebidas", label: "Bebidas" },
  { value: "comidas", label: "Comidas" },
  { value: "snacks", label: "Snacks y picadas" },
  { value: "postres", label: "Postres" },
  { value: "otros", label: "Otros productos" },
];

// =========================================================
// PRODUCT CARD
// =========================================================

function ProductCard({ product, onAddToCart }) {
  const image = getProductImage(product);
  const category = getProductCategory(product);

  const productName =
    product?.name ||
    product?.title ||
    "Producto de La Esquina del Parques";

  const productId =
    product?.id ||
    product?.pk ||
    product?.product_id ||
    product?.slug;

  const price = Number(
    product?.price ?? product?.sale_price ?? 0
  );

  const comparePrice = Number(
    product?.compare_price ??
      product?.original_price ??
      product?.old_price ??
      product?.regular_price ??
      0
  );

  const hasDiscount = comparePrice > price && price > 0;

  const discount = hasDiscount
    ? Math.round(((comparePrice - price) / comparePrice) * 100)
    : Number(product?.discount_percentage || 0);

  const stock =
    product?.stock === undefined || product?.stock === null
      ? null
      : Number(product.stock);

  const outOfStock = stock !== null && stock <= 0;

  const isFeatured = Boolean(
    product?.is_featured ||
      product?.featured ||
      product?.is_popular
  );

  const description =
    product?.short_description ||
    product?.description ||
    "Una opción para disfrutar en La Esquina del Parques.";

  const handleAdd = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!outOfStock) {
      onAddToCart(product);
    }
  };

  return (
    <article
      className={[
        "catalog-product-card",
        isFeatured ? "is-featured" : "",
        outOfStock ? "is-sold-out" : "",
      ].filter(Boolean).join(" ")}
    >
      <div className="catalog-product-visual">
        <Link
          to={productId ? `/products/${productId}` : "/catalog"}
          className="catalog-product-image-link"
          aria-label={`Ver ${productName}`}
        >
          <img
            src={image}
            alt={productName}
            loading="lazy"
            onError={(event) => {
              if (event.currentTarget.src !== catalogBg) {
                event.currentTarget.src = catalogBg;
              }
            }}
          />
        </Link>

        <div className="catalog-product-image-overlay" />

        <div className="catalog-product-badges">
          {isFeatured && (
            <span className="catalog-badge featured">
              <Sparkles size={12} />
              DESTACADO
            </span>
          )}

          {discount > 0 && (
            <span className="catalog-badge discount">
              -{discount}%
            </span>
          )}
        </div>

        <span className="catalog-product-index">
          {String(productId || "00").padStart(2, "0")}
        </span>

        {productId && (
          <Link
            to={`/products/${productId}`}
            className="catalog-quick-view"
            aria-label={`Ver ${productName}`}
          >
            <Eye size={16} />
            <span>VER PRODUCTO</span>
          </Link>
        )}

        {outOfStock && (
          <div className="catalog-product-soldout">
            AGOTADO
          </div>
        )}
      </div>

      <div className="catalog-product-content">
        <div className="catalog-product-topline">
          <span className="catalog-product-category">
            {category}
          </span>

          {product?.brand && (
            <span className="catalog-product-brand">
              {product.brand}
            </span>
          )}
        </div>

        <Link
          to={productId ? `/products/${productId}` : "/catalog"}
          className="catalog-product-title-link"
        >
          <h2>{productName}</h2>
        </Link>

        <p className="catalog-product-description">
          {description}
        </p>

        <div className="catalog-product-price-row">
          <div className="catalog-product-prices">
            {hasDiscount && (
              <span className="catalog-product-old-price">
                {formatPrice(comparePrice)}
              </span>
            )}

            <strong>
              {product?.formatted_price || formatPrice(price)}
            </strong>
          </div>

          {discount > 0 && (
            <span className="catalog-product-save">
              AHORRA {discount}%
            </span>
          )}
        </div>

        <div className="catalog-product-stock">
          <span className={outOfStock ? "stock-dot unavailable" : "stock-dot"} />

          <span>
            {outOfStock
              ? "Producto agotado"
              : stock !== null
                ? `Disponible · ${stock} unidades`
                : "Disponible"}
          </span>
        </div>

        <div className="catalog-product-actions">
          <button
            type="button"
            className="catalog-add-button"
            onClick={handleAdd}
            disabled={outOfStock}
          >
            <ShoppingCart size={17} />
            <span>{outOfStock ? "AGOTADO" : "AGREGAR AL CARRITO"}</span>
          </button>

          {productId && (
            <Link
              to={`/products/${productId}`}
              className="catalog-detail-button"
              aria-label={`Detalles de ${productName}`}
            >
              <ChevronRight size={19} />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

// =========================================================
// SIDEBAR
// =========================================================

function CatalogSidebar({
  categories,
  selectedCategory,
  onCategoryChange,
  search,
  onSearchChange,
}) {
  return (
    <aside className="catalog-sidebar">
      <section className="catalog-sidebar-block">
        <div className="catalog-sidebar-heading">
          <span>BUSCAR</span>
          <Search size={15} />
        </div>

        <div className="catalog-sidebar-search">
          <Search size={16} />

          <input
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Buscar bebidas, comidas..."
            aria-label="Buscar productos"
          />

          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              aria-label="Limpiar búsqueda"
            >
              <X size={15} />
            </button>
          )}
        </div>
      </section>

      <section className="catalog-sidebar-block">
        <div className="catalog-sidebar-heading">
          <span>CATEGORÍAS</span>
          <Tag size={15} />
        </div>

        <nav className="catalog-category-list">
          {categories.map((category) => (
            <button
              type="button"
              key={category.value || "all"}
              className={
                normalizeCategory(selectedCategory) ===
                normalizeCategory(category.value)
                  ? "active"
                  : ""
              }
              onClick={() => onCategoryChange(category.value)}
            >
              <span>{category.label}</span>
              <span className="category-count">{category.count}</span>
            </button>
          ))}
        </nav>
      </section>

      <section className="catalog-sidebar-editorial">
        <span>LA ESQUINA DEL PARQUES</span>

        <strong>
          BUEN SABOR.
          <br />
          BUENOS MOMENTOS.
        </strong>

        <p>
          Un lugar para compartir, disfrutar y convertir
          cualquier encuentro en un buen recuerdo.
        </p>

        <Link to="/blog">
          TORNEOS, EVENTOS Y NOVEDADES
          <ArrowRight size={15} />
        </Link>
      </section>

      <section className="catalog-sidebar-trust">
        <div>
          <Package size={17} />
          <span>
            <strong>VARIEDAD</strong>
            Opciones para disfrutar
          </span>
        </div>

        <div>
          <Check size={17} />
          <span>
            <strong>CALIDAD</strong>
            Selección para compartir
          </span>
        </div>

        <div>
          <ShoppingCart size={17} />
          <span>
            <strong>ATENCIÓN</strong>
            Servicio cercano
          </span>
        </div>
      </section>
    </aside>
  );
}

// =========================================================
// MAIN COMPONENT
// =========================================================

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState(
    searchParams.get("search") || ""
  );

  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || ""
  );

  const [sortBy, setSortBy] = useState("featured");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [addedProduct, setAddedProduct] = useState(null);

  // =======================================================
  // LOAD PRODUCTS
  // =======================================================

  useEffect(() => {
    let mounted = true;

    async function loadProducts() {
      try {
        setLoading(true);
        setError("");

        const response = await API.get("/products/");
        const productList = getResults(response?.data);

        if (mounted) {
          setProducts(productList);
        }
      } catch (err) {
        console.error("Error cargando catálogo:", err);

        if (mounted) {
          setError(
            err?.response?.data?.detail ||
              err?.response?.data?.message ||
              "No fue posible cargar el catálogo."
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      mounted = false;
    };
  }, []);

  // =======================================================
  // SYNC FILTERS WITH URL
  // =======================================================

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);

    if (search.trim()) {
      nextParams.set("search", search.trim());
    } else {
      nextParams.delete("search");
    }

    if (selectedCategory) {
      nextParams.set("category", selectedCategory);
    } else {
      nextParams.delete("category");
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [search, selectedCategory, searchParams, setSearchParams]);

  // =======================================================
  // CATEGORIES
  // =======================================================

  const categories = useMemo(() => {
    return CATEGORY_OPTIONS.map((category) => {
      if (!category.value) {
        return { ...category, count: products.length };
      }

      const count = products.filter(
        (product) =>
          normalizeCategory(getProductCategory(product)) ===
          normalizeCategory(category.value)
      ).length;

      return { ...category, count };
    });
  }, [products]);

  // =======================================================
  // FILTER + SORT
  // =======================================================

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (selectedCategory) {
      const normalized = normalizeCategory(selectedCategory);

      result = result.filter((product) => {
        const category = normalizeCategory(getProductCategory(product));

        return (
          category === normalized ||
          category.includes(normalized) ||
          normalized.includes(category)
        );
      });
    }

    if (search.trim()) {
      const query = normalizeCategory(search);

      result = result.filter((product) => {
        const haystack = [
          product?.name,
          product?.title,
          product?.description,
          product?.short_description,
          product?.sku,
          product?.code,
          getProductCategory(product),
        ]
          .filter(Boolean)
          .join(" ");

        return normalizeCategory(haystack).includes(query);
      });
    }

    result.sort((a, b) => {
      const priceA = Number(a?.price ?? a?.sale_price ?? 0);
      const priceB = Number(b?.price ?? b?.sale_price ?? 0);

      switch (sortBy) {
        case "price-low":
          return priceA - priceB;

        case "price-high":
          return priceB - priceA;

        case "name":
          return String(a?.name || a?.title || "").localeCompare(
            String(b?.name || b?.title || ""),
            "es"
          );

        case "popular":
          return (
            Number(b?.selection_count || b?.views || 0) -
            Number(a?.selection_count || a?.views || 0)
          );

        default:
          return (
            Number(Boolean(b?.is_featured || b?.featured)) -
            Number(Boolean(a?.is_featured || a?.featured))
          );
      }
    });

    return result;
  }, [products, selectedCategory, search, sortBy]);

  // =======================================================
  // ADD TO CART
  // =======================================================

  const handleAddToCart = (product) => {
    if (!product) return;

    const stock =
      product?.stock === undefined || product?.stock === null
        ? null
        : Number(product.stock);

    if (stock !== null && stock <= 0) return;

    const productId = product?.id || product?.pk || product?.product_id;
    if (!productId) return;

    const price = Number(product?.price ?? product?.sale_price ?? 0);

    const cartItem = {
      id: productId,
      name: product?.name || product?.title || "Producto",
      price,
      image: getProductImage(product),
      qty: 1,
    };

    try {
      const parsed = JSON.parse(localStorage.getItem("cart") || "[]");
      const cart = Array.isArray(parsed) ? parsed : [];

      const existingIndex = cart.findIndex(
        (item) => String(item?.id) === String(productId)
      );

      if (existingIndex >= 0) {
        const currentQty = Number(cart[existingIndex]?.qty || 0);

        const nextQty =
          stock !== null
            ? Math.min(currentQty + 1, stock)
            : currentQty + 1;

        cart[existingIndex] = {
          ...cart[existingIndex],
          qty: nextQty,
        };
      } else {
        cart.push(cartItem);
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      window.dispatchEvent(new Event("cartUpdated"));
      setAddedProduct(product);

      window.setTimeout(() => {
        setAddedProduct(null);
      }, 2200);
    } catch (err) {
      console.error("Error agregando al carrito:", err);
    }
  };

  // =======================================================
  // FILTER HANDLERS
  // =======================================================

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setMobileFiltersOpen(false);
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedCategory("");
    setSortBy("featured");
  };

  const resultLabel = useMemo(() => {
    if (search.trim()) {
      return `RESULTADOS PARA "${search.trim()}"`;
    }

    if (selectedCategory) {
      const selected = CATEGORY_OPTIONS.find(
        (item) =>
          normalizeCategory(item.value) ===
          normalizeCategory(selectedCategory)
      );

      return selected?.label?.toUpperCase() || "CATÁLOGO";
    }

    return "SELECCIÓN DE LA CASA";
  }, [search, selectedCategory]);

  // =======================================================
  // HERO CONTENT
  // =======================================================

  const heroEyebrow =
    "LA ESQUINA DEL PARQUES · BAR & GASTROBAR";

  const heroTitle = "SABOR, ENCUENTROS";
  const heroHighlight = "Y BUENOS MOMENTOS";

  const heroText =
    "Descubre nuestra selección, conoce las novedades y encuentra el plan perfecto para compartir.";

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <>
      <Navbar />

      <main
        className="catalog-page"
        style={{ "--catalog-bg": `url(${catalogBg})` }}
      >
        {/* HERO / SLIDER VISUAL */}

        <section
          className="catalog-hero blog-style-hero"
          style={{ backgroundImage: `url(${catalogBg})` }}
        >
          <div className="catalog-hero-overlay" />

          <div className="catalog-hero-inner">
            <div className="catalog-breadcrumb">
              <Link to="/">INICIO</Link>
              <ChevronRight size={13} />
              <span>CATÁLOGO</span>

              {selectedCategory && (
                <>
                  <ChevronRight size={13} />
                  <strong>
                    {CATEGORY_OPTIONS.find(
                      (item) =>
                        normalizeCategory(item.value) ===
                        normalizeCategory(selectedCategory)
                    )?.label || selectedCategory}
                  </strong>
                </>
              )}
            </div>

            <div className="catalog-hero-copy">
              <span className="catalog-hero-kicker">
                {heroEyebrow}
              </span>

              <h1>
                {heroTitle}
                <em>{heroHighlight}</em>
              </h1>

              <p>{heroText}</p>

              <Link to="#catalogo" className="catalog-hero-cta">
                EXPLORAR CATÁLOGO
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="catalog-hero-index">
              <span>BUENOS MOMENTOS</span>
              <strong>LA ESQUINA</strong>
            </div>
          </div>
        </section>

        {/* CATALOG LAYOUT */}

        <section className="catalog-layout" id="catalogo">
          <div className="catalog-mobile-toolbar">
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((value) => !value)}
            >
              <SlidersHorizontal size={17} />
              FILTROS
            </button>

            <span>{filteredProducts.length} PRODUCTOS</span>
          </div>

          <CatalogSidebar
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={handleCategoryChange}
            search={search}
            onSearchChange={setSearch}
          />

          <div className="catalog-content">
            <header className="catalog-content-header">
              <div className="catalog-heading">
                <span className="catalog-section-kicker">
                  LA ESQUINA DEL PARQUES / GASTROBAR
                </span>

                <h2>{resultLabel}</h2>

                <p>
                  Nuestra selección · {filteredProducts.length} productos
                </p>
              </div>

              <div className="catalog-sort">
                <label htmlFor="catalog-sort">ORDENAR</label>

                <div className="catalog-sort-select">
                  <select
                    id="catalog-sort"
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value)}
                  >
                    <option value="featured">Recomendados</option>
                    <option value="popular">Más vistos</option>
                    <option value="price-low">Precio menor</option>
                    <option value="price-high">Precio mayor</option>
                    <option value="name">Nombre</option>
                  </select>

                  <ChevronDown size={14} />
                </div>
              </div>
            </header>

            {(search || selectedCategory) && (
              <div className="catalog-active-filters">
                <span>FILTROS ACTIVOS</span>

                {selectedCategory && (
                  <button
                    type="button"
                    onClick={() => setSelectedCategory("")}
                  >
                    {CATEGORY_OPTIONS.find(
                      (item) =>
                        normalizeCategory(item.value) ===
                        normalizeCategory(selectedCategory)
                    )?.label || selectedCategory}
                    <X size={12} />
                  </button>
                )}

                {search && (
                  <button type="button" onClick={() => setSearch("")}>
                    "{search}"
                    <X size={12} />
                  </button>
                )}

                <button
                  type="button"
                  className="clear-all"
                  onClick={clearFilters}
                >
                  LIMPIAR TODO
                </button>
              </div>
            )}

            {loading && (
              <div className="catalog-loading">
                <div className="catalog-loader-ring" />
                <span>CARGANDO CATÁLOGO</span>
                <small>LA ESQUINA DEL PARQUES · BAR</small>
              </div>
            )}

            {!loading && error && (
              <div className="catalog-state catalog-error">
                <span className="state-label">CATÁLOGO</span>
                <h3>No pudimos cargar el catálogo.</h3>
                <p>{error}</p>

                <button
                  type="button"
                  onClick={() => window.location.reload()}
                >
                  REINTENTAR
                </button>
              </div>
            )}

            {!loading && !error && filteredProducts.length === 0 && (
              <div className="catalog-state catalog-empty">
                <span className="state-label">CATÁLOGO / 00</span>
                <h3>No encontramos productos.</h3>
                <p>
                  Prueba otra búsqueda o selecciona una categoría diferente.
                </p>

                <button type="button" onClick={clearFilters}>
                  VER TODO EL CATÁLOGO
                </button>
              </div>
            )}

            {!loading && !error && filteredProducts.length > 0 && (
              <div className="catalog-product-grid">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={
                      product?.id ||
                      product?.pk ||
                      product?.product_id ||
                      product?.slug ||
                      product?.name
                    }
                    product={product}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            )}

            {!loading && !error && filteredProducts.length > 0 && (
              <section className="catalog-bottom-editorial">
                <div className="catalog-bottom-editorial-title">
                  <span>LA ESQUINA DEL PARQUES</span>
                  <strong>SABOR PARA COMPARTIR.</strong>
                </div>

                <p>
                  Descubre nuestra selección y encuentra tus favoritos
                  para disfrutar en buena compañía.
                </p>

                <Link to="/blog">
                  TORNEOS, EVENTOS Y NOVEDADES
                  <ArrowRight size={15} />
                </Link>
              </section>
            )}
          </div>
        </section>

        {/* MOBILE FILTER DRAWER */}

        {mobileFiltersOpen && (
          <div
            className="catalog-mobile-filter-overlay"
            onClick={() => setMobileFiltersOpen(false)}
          >
            <div
              className="catalog-mobile-filter-panel"
              onClick={(event) => event.stopPropagation()}
            >
              <header>
                <div>
                  <span>LA ESQUINA DEL PARQUES</span>
                  <h2>FILTROS</h2>
                </div>

                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  aria-label="Cerrar filtros"
                >
                  <X size={20} />
                </button>
              </header>

              <div className="mobile-filter-search">
                <Search size={17} />

                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar bebidas, comidas..."
                />
              </div>

              <div className="mobile-filter-categories">
                <span className="mobile-filter-label">
                  CATEGORÍAS
                </span>

                {categories.map((category) => (
                  <button
                    type="button"
                    key={category.value || "all"}
                    className={
                      normalizeCategory(selectedCategory) ===
                      normalizeCategory(category.value)
                        ? "active"
                        : ""
                    }
                    onClick={() => handleCategoryChange(category.value)}
                  >
                    <span>{category.label}</span>
                    <span>{category.count}</span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="mobile-filter-clear"
                onClick={() => {
                  clearFilters();
                  setMobileFiltersOpen(false);
                }}
              >
                LIMPIAR FILTROS
              </button>
            </div>
          </div>
        )}

        {/* CART TOAST */}

        {addedProduct && (
          <div className="catalog-cart-toast">
            <div className="cart-toast-icon">
              <Check size={18} />
            </div>

            <div>
              <span>AGREGADO AL CARRITO</span>
              <strong>
                {addedProduct.name || addedProduct.title || "Producto"}
              </strong>
            </div>

            <Link to="/cart" className="cart-toast-link">
              VER CARRITO
              <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}
